<template>
  <v-container fluid>
    <v-card elevation="2">
      <!-- ——— header ——— -->
      <v-card-title class="d-flex align-center tableheader white--text">
        <v-icon left color="white">mdi-flag</v-icon>
        Countries
        <v-spacer/>
        <v-text-field
          v-model="search"
          label="Search (country name, code)"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="solo-filled"
          flat hide-details single-line
          class="me-4"
        />
        <v-btn color="primary" @click="openDialog(null)" v-if="authStore.canEdit">
          <v-icon left>mdi-plus</v-icon> Add Country
        </v-btn>

        <ExportButtons entity="countries" filename="Countries" :headers="headers"></ExportButtons>
      </v-card-title>

      <!-- ——— table ——— -->
      <v-data-table :items="filtered" :headers="headers" class="elevation-1">
        <template v-slot:[`item.phone_code`]="{ value }">
          {{ value }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          
          <v-tooltip location="top" text="View Details">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" small class="me-1" @click="openViewDialog(item)" color="info">
              mdi-eye-outline
            </v-icon>
          </template>
          </v-tooltip>

          <v-tooltip location="top" text="Edit">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" small class="me-1" @click="openDialog(item)" color="secondary" v-if="authStore.isAdmin">
              mdi-pencil
            </v-icon>
          </template>
          </v-tooltip>

          <v-tooltip location="top" text="Delete">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" small @click="deleteItemPrompt(item)" color="error" v-if="authStore.isAdmin">
              mdi-delete
            </v-icon>
          </template>
          </v-tooltip>

        </template>
        <template v-slot:[`no-data`]>
          <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
            {{ tableLoading ? 'Fetching data...' : 'No countries available or match your search.' }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>

    <!-- ——— dialog ——— -->
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>{{ form.id ? 'Edit' : 'Add' }} Country</v-card-title>
        <v-form ref="countryForm" v-model="isFormValid" @submit.prevent="save">
        <v-card-text>
          <v-text-field
            v-model="form.country_code"
            label="Country Code*"
            maxlength="3"
            class="mb-2"
            variant="outlined"
            :rules="[rules.required, rules.countryCode]"
            required
            hint="3-letter country code (e.g., USA, GBR)"
            persistent-hint
          />

          <v-text-field
            v-model="form.country_name"
            label="Country Name*"
            maxlength="255"
            class="mb-2"
            variant="outlined"
            :rules="[rules.required]"
            required
          />

          <v-text-field
            v-model="form.phone_code"
            label="Phone Code*"
            maxlength="10"
            class="mb-2"
            variant="outlined"
            :rules="[rules.required, rules.phoneCode]"
            required
            hint="International calling code (e.g., 1, 44, 33)"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="elevated" type="submit" :loading="dialogLoading" :disabled="!isFormValid">Save</v-btn>
        </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="800px" @update:modelValue="val => !val && closeViewDialog()">
    <v-card :loading="viewDialogLoading">
      <v-card-title class="tableheader white--text d-flex align-center">
        <v-icon left color="white" class="mr-2">mdi-flag</v-icon>
        <span class="text-h5">Country Details: {{ viewItem?.country_name }}</span>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" color="white" @click="closeViewDialog"></v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text v-if="viewItem" class="pt-4">
        <v-list lines="two" density="compact">
          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Country Code:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ viewItem.country_code }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>
          
          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Country Name:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ viewItem.country_name }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>
          
          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Phone Code:</div>
            </template>
            <v-list-item-text class="text-right pl-2">+{{ viewItem.phone_code }}</v-list-item-text>
          </v-list-item><v-divider inset></v-divider>
        </v-list>
      </v-card-text>
      <v-card-text v-else class="text-center py-5">
          <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
          <p>Loading country details...</p>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="bg-grey-lighten-4 pa-3">
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="tonal" @click="closeViewDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card :loading="dialogLoading">
      <v-card-title class="text-h5 error--text">Confirm Deletion</v-card-title>
      <v-card-text>Are you sure you want to delete country: <strong>{{ itemToDelete?.country_name }}</strong>?</v-card-text>
      <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
          <v-spacer></v-spacer>
      </v-card-actions>
      </v-card>
  </v-dialog>

  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
    {{ snackbar.message }}
    <template v-slot:actions>
      <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
    </template>
  </v-snackbar>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import CountriesService from '@/services/CountriesService';
import { useAuthStore } from '@/stores/auth';
import { useSnackbar } from '@/services/genFunctions';
import ExportButtons from '@/components/ExportButtons.vue';

const authStore = useAuthStore();
/* ——— state ——— */
const countries = ref([]);

const viewDialogLoading = ref(false);
const viewDialog = ref(false);
const viewItem = ref(null);
const itemToDelete = ref(null);
const deleteDialog = ref(false);
const dialogLoading = ref(false);
const tableLoading = ref(true);
const countryForm = ref(null);
const isFormValid = ref(false);

const rules = {
  required: value => !!value || 'This field is required.',
  countryCode: value => /^[A-Z]{3}$/.test(value || '') || 'Country code must be exactly 3 uppercase letters.',
  phoneCode: value => /^\d{1,10}$/.test(value || '') || 'Phone code must contain only digits (1-10 characters).',
};

const { snackbar, showSnackbar } = useSnackbar();

const search = ref('');
const dialog = ref(false);
const form   = ref(resetForm());

const headers = [
  { title: 'Country Code', key: 'country_code', width: '150' },
  { title: 'Country Name', key: 'country_name', width: '200' },
  { title: 'Phone Code', key: 'phone_code', width: '120' },
  { title: 'Actions', key: 'actions', sortable: false, width: '120' }
];

/* ——— lifecycle ——— */

onMounted(async () => {
  tableLoading.value = true;
  try {
    await loadAll();
  } catch (error) {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        showSnackbar(error.response.data.error || 'You are not authorized to view countries.', 'error');
    } else {
        showSnackbar('Failed to load countries.', 'error');
    }
    console.error("Error fetching countries:", error);
    countries.value = [];
  } finally {
    tableLoading.value = false;
  }
});

async function loadAll() {
  countries.value = await CountriesService.getCountries();
}

/* ——— helpers ——— */
function resetForm() {
  return {
    id: null,
    country_code: '',
    country_name: '',
    phone_code: ''
  };
}

const filtered = computed(() => {
  const q = search.value.toLowerCase();
  return countries.value.filter(c =>
    c.country_name.toLowerCase().includes(q) ||
    c.country_code.toLowerCase().includes(q)
  );
});

/* ——— crud ——— */
function openDialog(item) {
  form.value = item ? { ...item } : resetForm();
  dialog.value = true;
}

async function save() {
  if (!countryForm.value) return;
  const { valid } = await countryForm.value.validate();
  if (!valid) {
    showSnackbar('Please correct the form errors.', 'warning');
    return;
  }
  const { id, ...data } = form.value;
  try {
    if (id) {
      await CountriesService.updateCountry(id, data);
      showSnackbar('Country updated successfully!', 'success');
    } else {
      await CountriesService.createCountry(data);
      showSnackbar('Country created successfully!', 'success');
    }
    dialog.value = false;
    await loadAll();
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Failed to save country.';
    showSnackbar(errorMsg, 'error');
    console.error("Error saving country:", error);
  }
}

const deleteItemPrompt = (item) => {
  itemToDelete.value = { ...item };
  deleteDialog.value = true;
};

const closeDeleteDialog = () => {
  deleteDialog.value = false;
  nextTick(() => itemToDelete.value = null);
  dialogLoading.value = false;
};

const confirmDelete = async () => {
  if (!itemToDelete.value?.id) return;
  dialogLoading.value = true;
  try {
      await CountriesService.deleteCountry(itemToDelete.value.id);
      countries.value = countries.value.filter(c => c.id !== itemToDelete.value.id);
      showSnackbar('Country deleted successfully!', 'success');
      closeDeleteDialog();
  } catch (error) {
      const errorMsg = error.response?.data?.error || 'Could not delete country.';
      showSnackbar(errorMsg, 'error', 6000);
      console.error("Error deleting country:", error.response || error);
  } finally {
      dialogLoading.value = false;
  }
};

const openViewDialog = async (item) => {
  viewDialogLoading.value = true;
  viewDialog.value = true;
  viewItem.value = null; 
  try {
    const fullCountryData = await CountriesService.getCountry(item.id);
    viewItem.value = fullCountryData;
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Failed to load country details.';
    showSnackbar(errorMsg, 'error');
    console.error("Error fetching country details for view:", error);
    viewDialog.value = false; 
  } finally {
    viewDialogLoading.value = false;
  }
};

const closeViewDialog = () => {
  viewDialog.value = false;
};
</script>
